import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { logWarn } from "@/lib/log";

// Continuous 30-juz murottal (Khalifa Al Tunaiji): one <audio> per surah (full-surah mp3 from mp3quran CDN)
// + per-ayah timing from /api/quran/murottal/{surah} → current ayah, seek-by-ayah, auto-advance to the next surah.
const POS_KEY = "murottal_pos";
const RATE_KEY = "murottal_rate";
const RECITER_KEY = "murottal_reciter";
export const RATES = [0.75, 1, 1.25, 1.5];
export const RECITERS = [
  { id: "tunaiji", name: "Khalifa Al Tunaiji", ar: "خليفة الطنيجي" },
  { id: "alafasy", name: "Mishary Rashid Alafasy", ar: "مشاري راشد العفاسي" },
  { id: "husary", name: "Mahmoud Khalil Al-Husary", ar: "محمود خليل الحصري" },
  { id: "minshawi", name: "Muhammad Siddiq Al-Minshawi", ar: "محمد صديق المنشاوي" },
];
export const SLEEP_MINUTES = [15, 30, 60];
// First (surah, ayah) of each juz 1..30.
export const JUZ_STARTS = [[1, 1], [2, 142], [2, 253], [3, 93], [4, 24], [4, 148], [5, 82], [6, 111], [7, 88], [8, 41], [9, 93], [11, 6], [12, 53], [15, 1], [17, 1], [18, 75], [21, 1], [23, 1], [25, 21], [27, 56], [29, 46], [33, 31], [36, 28], [39, 32], [41, 47], [46, 1], [51, 31], [58, 1], [67, 1], [78, 1]];

const Ctx = createContext(null);
const readPos = () => { try { const p = JSON.parse(localStorage.getItem(POS_KEY)); return p?.surah ? p : null; } catch { return null; } };
const readRate = () => { const r = Number(localStorage.getItem(RATE_KEY)); return RATES.includes(r) ? r : 1; };
const readReciter = () => { const r = localStorage.getItem(RECITER_KEY); return RECITERS.some((x) => x.id === r) ? r : "tunaiji"; };
const timingCache = new Map();

async function loadTiming(surah, reciter) {
  const key = `${reciter}:${surah}`;
  if (!timingCache.has(key)) timingCache.set(key, api.get(`/quran/murottal/${surah}`, { params: { reciter } }).then((r) => r.data).catch((e) => { timingCache.delete(key); throw e; }));
  return timingCache.get(key);
}
const ayahAt = (timing, ms) => {
  let cur = timing[0]?.ayah || 1;
  for (const t of timing) { if (ms >= t.start) cur = t.ayah; else break; }
  return cur;
};

export const MurottalProvider = ({ children }) => {
  const [surahs, setSurahs] = useState([]);
  const [surah, setSurah] = useState(null);
  const [ayah, setAyah] = useState(1);
  const [status, setStatus] = useState("idle"); // idle | loading | playing | paused | error | finished
  const [progress, setProgress] = useState({ t: 0, d: 0 });
  const [rate, setRateState] = useState(readRate);
  const [repeat, setRepeat] = useState(false);
  const [saved, setSaved] = useState(readPos);
  const [reciter, setReciterState] = useState(readReciter);
  const [sleep, setSleep] = useState(null); // null | { mode: "min", until } | { mode: "surah" }
  const [sleepLeft, setSleepLeft] = useState(0);
  const [sleptOff, setSleptOff] = useState(false);
  const audioRef = useRef(null);
  const timingRef = useRef([]);
  const surahRef = useRef(null);
  const ayahRef = useRef(1);
  const repeatRef = useRef(false);
  const rateRef = useRef(rate);
  const reciterRef = useRef(reciter);
  const sleepRef = useRef(null);
  const startRef = useRef(null);
  repeatRef.current = repeat;
  rateRef.current = rate;
  reciterRef.current = reciter;
  sleepRef.current = sleep;

  useEffect(() => { api.get("/quran/surahs").then((r) => setSurahs(r.data.data || [])).catch(() => {}); }, []);

  const audio = () => {
    if (!audioRef.current) {
      const a = new Audio();
      a.preload = "auto";
      a.addEventListener("timeupdate", () => {
        const ms = a.currentTime * 1000;
        setProgress({ t: a.currentTime, d: a.duration || 0 });
        if (timingRef.current.length) setAyah((cur) => { const n = ayahAt(timingRef.current, ms); ayahRef.current = n; return n === cur ? cur : n; });
        if (surahRef.current && Math.floor(a.currentTime) % 5 === 0) {
          const p = { surah: surahRef.current, time: a.currentTime, ayah: ayahAt(timingRef.current, ms) };
          localStorage.setItem(POS_KEY, JSON.stringify(p));
        }
      });
      a.addEventListener("play", () => setStatus("playing"));
      a.addEventListener("pause", () => { if (!a.ended) setStatus((s) => (s === "loading" ? s : "paused")); });
      a.addEventListener("waiting", () => setStatus("loading"));
      a.addEventListener("playing", () => setStatus("playing"));
      a.addEventListener("error", () => { setStatus("error"); logWarn("Murottal audio error", a.error); });
      a.addEventListener("ended", () => {
        const s = surahRef.current;
        if (sleepRef.current?.mode === "surah") { setSleep(null); setStatus("paused"); setSleptOff(true); return; }
        if (repeatRef.current) { a.currentTime = 0; a.play().catch(() => {}); return; }
        if (s < 114) startRef.current(s + 1, 1); else { setStatus("finished"); localStorage.removeItem(POS_KEY); setSaved(null); }
      });
      audioRef.current = a;
    }
    return audioRef.current;
  };

  const startSurah = useCallback(async (n, fromAyah = 1, atTime = null) => {
    const a = audio();
    setStatus("loading");
    setSurah(n); surahRef.current = n; setAyah(fromAyah); ayahRef.current = fromAyah;
    const rec = reciterRef.current;
    try {
      const data = await loadTiming(n, rec);
      if (surahRef.current !== n || reciterRef.current !== rec) return;
      timingRef.current = data.timing;
      const start = atTime != null ? atTime : fromAyah > 1 ? (data.timing.find((t) => t.ayah === fromAyah)?.start || 0) / 1000 : 0;
      a.src = data.audio;
      a.playbackRate = rateRef.current;
      a.load();
      if (start > 0) await new Promise((res, rej) => {
        const ok = () => { cleanup(); a.currentTime = start; res(); };
        const bad = () => { cleanup(); rej(new Error("load")); };
        const cleanup = () => { a.removeEventListener("loadedmetadata", ok); a.removeEventListener("error", bad); };
        a.addEventListener("loadedmetadata", ok); a.addEventListener("error", bad);
      });
      if (surahRef.current !== n) return;
      await a.play();
      setSaved({ surah: n, ayah: fromAyah, time: start });
    } catch (e) {
      if (e?.name === "AbortError") return;
      setStatus("error"); logWarn("Murottal start failed", e);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  startRef.current = startSurah;

  const seekAyah = useCallback((target) => {
    const a = audioRef.current; if (!a || !timingRef.current.length) return;
    const t = timingRef.current.find((x) => x.ayah === target); if (!t) return;
    a.currentTime = t.start / 1000; setAyah(target);
    if (a.paused) a.play().catch(() => {});
  }, []);

  const api_ = useMemo(() => ({
    surahs, surah, ayah, status, progress, rate, repeat, saved, reciter, sleep, sleepLeft, sleptOff,
    reciterMeta: RECITERS.find((r) => r.id === reciter) || RECITERS[0],
    surahMeta: surahs.find((s) => s.nomor === surah) || null,
    totalAyah: timingRef.current.length || surahs.find((s) => s.nomor === surah)?.jumlahAyat || 0,
    play: (n, fromAyah = 1) => { setSleptOff(false); return startSurah(n, fromAyah); },
    playJuz: (j) => { const [s, ay] = JUZ_STARTS[j - 1]; setSleptOff(false); return startSurah(s, ay); },
    resume: () => { const p = readPos(); if (p) { setSleptOff(false); startSurah(p.surah, p.ayah || 1, p.time || null); } },
    toggle: () => { const a = audioRef.current; if (!a || !surahRef.current) return; setSleptOff(false); a.paused ? a.play().catch(() => {}) : a.pause(); },
    seekAyah,
    nextAyah: () => { if (ayah < timingRef.current.length) seekAyah(ayah + 1); else if (surah < 114) startSurah(surah + 1, 1); },
    prevAyah: () => { if (ayah > 1) seekAyah(ayah - 1); else if (surah > 1) startSurah(surah - 1, 1); },
    nextSurah: () => surah < 114 && startSurah(surah + 1, 1),
    prevSurah: () => surah > 1 && startSurah(surah - 1, 1),
    seek: (sec) => { const a = audioRef.current; if (a) a.currentTime = sec; },
    setRate: (r) => { setRateState(r); localStorage.setItem(RATE_KEY, String(r)); if (audioRef.current) audioRef.current.playbackRate = r; },
    setRepeat,
    // Switch reciter; if something is playing, restart the same surah at the current ayah so highlighting stays in sync.
    setReciter: (id) => {
      if (!RECITERS.some((r) => r.id === id)) return;
      setReciterState(id); reciterRef.current = id; localStorage.setItem(RECITER_KEY, id);
      if (surahRef.current && status !== "idle") startSurah(surahRef.current, ayahRef.current);
    },
    setSleep: (mode, minutes = 0) => {
      if (!mode) { setSleep(null); return; }
      setSleptOff(false);
      setSleep(mode === "surah" ? { mode: "surah" } : { mode: "min", minutes, until: Date.now() + minutes * 60000 });
    },
    stop: () => { const a = audioRef.current; if (a) { a.pause(); a.removeAttribute("src"); a.load(); } surahRef.current = null; timingRef.current = []; setSurah(null); setStatus("idle"); setProgress({ t: 0, d: 0 }); setSaved(readPos()); setSleep(null); setSleptOff(false); },
  }), [surahs, surah, ayah, status, progress, rate, repeat, saved, reciter, sleep, sleepLeft, sleptOff, startSurah, seekAyah]);

  // Sleep timer: countdown + pause when due (the "end of surah" mode is handled in the `ended` listener).
  useEffect(() => {
    if (!sleep) { setSleepLeft(0); return; }
    const tick = () => {
      if (sleep.mode !== "min") return;
      const left = Math.max(0, Math.round((sleep.until - Date.now()) / 1000));
      setSleepLeft(left);
      if (left <= 0) { audioRef.current?.pause(); setSleep(null); setSleptOff(true); }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sleep]);

  // Lock-screen / headset controls.
  useEffect(() => {
    if (!("mediaSession" in navigator) || !surah) return;
    const meta = surahs.find((s) => s.nomor === surah);
    const rec = RECITERS.find((r) => r.id === reciter) || RECITERS[0];
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({ title: meta ? `${meta.namaLatin} (${meta.nama})` : `Surah ${surah}`, artist: rec.name, album: "Murottal Al-Qur'an 30 Juz", artwork: [{ src: "/icon-192.png", sizes: "192x192", type: "image/png" }] });
      navigator.mediaSession.setActionHandler("play", api_.toggle);
      navigator.mediaSession.setActionHandler("pause", api_.toggle);
      navigator.mediaSession.setActionHandler("previoustrack", api_.prevAyah);
      navigator.mediaSession.setActionHandler("nexttrack", api_.nextAyah);
    } catch { /* unsupported */ }
  }, [surah, surahs, reciter, api_]);

  return <Ctx.Provider value={api_}>{children}</Ctx.Provider>;
};

export const useMurottal = () => useContext(Ctx);
